import React, { useMemo, useState } from 'react';
import { useMailProvider } from './MailProviderContext';
import type { MailThread } from '../../lib/stores/mail/types';
import type { MailOpenInfo } from '../../lib/hooks/useMailOpenStatus';
import { Star, Mail, MailOpen, Archive, Trash2, Paperclip, Check, Eye } from 'lucide-react';
import { LabelChips, AttachmentChips } from './MailRowChips';
import { pickThreadClient } from '../../lib/mail/threadLink';
import { useInternalEmails } from '../../lib/mail/useInternalEmails';

// Accento brand Flusia (rosa → arancio), usato con parsimonia
export const FLUSIA_GRADIENT = 'linear-gradient(135deg, #E91E8C, #F97316)';

// Palette avatar deterministica, tinte tenui (rese "meno piene" via sfondo soft + testo colorato)
const AVATAR_COLORS = [
  '#E91E8C', '#F97316', '#2383E2', '#0F7B6C',
  '#7C3AED', '#D9730D', '#DC2626', '#0891B2',
];
function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

interface ThreadListItemProps {
  thread: MailThread;
  isSelected: boolean;
  organizationId: string;
  userId: string;
  fullWidth?: boolean;
  onClick: () => void;
  checked?: boolean;
  selectionActive?: boolean;
  onToggleCheck?: (e: React.MouseEvent) => void;
  // Stato apertura (tracking pixel) aggregato del thread; undefined = nessuna apertura
  openInfo?: MailOpenInfo;
}

export function MailThreadListItem({
  thread,
  isSelected,
  organizationId,
  userId,
  fullWidth = false,
  onClick,
  checked = false,
  selectionActive = false,
  onToggleCheck,
  openInfo,
}: ThreadListItemProps) {
  const { useStore } = useMailProvider();
  const { archiveThread, trashThread, markAsRead, markAsUnread, toggleStar } = useStore();
  const hasDraft = useStore(s => !!s.drafts[thread.thread_id]);
  const [busy, setBusy] = useState(false);

  const checkbox = onToggleCheck ? (
    <button
      type="button"
      onClick={onToggleCheck}
      title={checked ? 'Deseleziona' : 'Seleziona'}
      className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
        checked
          ? 'bg-[#2383E2] border-[#2383E2] text-white'
          : 'border-[#C4C4C0] dark:border-white/30 hover:border-[#2383E2]'
      } ${checked || selectionActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
    >
      {checked && <Check className="h-3 w-3" />}
    </button>
  ) : null;

  const latest = thread.latest_message;
  const date = new Date(latest.internal_date);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const dateStr = isToday
    ? date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });

  // Scheda del thread con la stessa regola della schermata di dettaglio: il badge
  // mostrava la scheda dell'ultimo messaggio, spesso quella dell'utente stesso.
  const internalEmails = useInternalEmails(organizationId);
  const linkedClient = useMemo(
    () => pickThreadClient(thread.messages, internalEmails),
    [thread.messages, internalEmails],
  );

  const senderName = latest.from_name || latest.from_email.split('@')[0];
  const initial = (linkedClient?.business_name || linkedClient?.contact_name || senderName || '?')
    .charAt(0)
    .toUpperCase();
  const color = avatarColor(latest.from_email || senderName);
  // Allegato: il flag has_attachments non è sempre valorizzato dal sync (formato metadata),
  // quindi controlliamo anche i metadati allegati presenti su uno qualsiasi dei messaggi.
  const hasAttachment = thread.messages.some(
    m => m.has_attachments || (m.attachments_metadata?.length ?? 0) > 0
  );

  // Glifo apertura (tracking pixel): occhio verde con conteggio + ultima apertura nel tooltip
  const openGlyph = openInfo ? (
    <span
      className="flex-shrink-0 inline-flex items-center"
      title={`Aperta ${openInfo.openCount}×${
        openInfo.lastOpenedAt
          ? ` — ultima: ${new Date(openInfo.lastOpenedAt).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
          : ''
      }`}
    >
      <Eye className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
    </span>
  ) : null;

  const runAction = (e: React.MouseEvent, fn: () => Promise<unknown>) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    Promise.resolve(fn()).finally(() => setBusy(false));
  };

  const toggleRead = (e: React.MouseEvent) => {
    const ids = thread.messages.map(m => m.id);
    runAction(e, () =>
      thread.has_unread
        ? markAsRead(organizationId, userId, ids)
        : markAsUnread(organizationId, userId, ids)
    );
  };

  const handleStar = (e: React.MouseEvent) =>
    runAction(e, () => toggleStar(organizationId, userId, thread.thread_id, !thread.is_starred));

  const quickActions = (
    <div className="hidden group-hover:flex items-center gap-0.5 rounded-md bg-white dark:bg-gray-900 shadow-sm border border-[rgba(55,53,47,0.09)] dark:border-gray-700 px-0.5 py-0.5">
      <QuickAction icon={<Archive className="h-3.5 w-3.5" />} title="Archivia" onClick={(e) => runAction(e, () => archiveThread(organizationId, userId, thread.thread_id))} disabled={busy} />
      <QuickAction
        icon={thread.has_unread ? <MailOpen className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
        title={thread.has_unread ? 'Segna come letto' : 'Segna come non letto'}
        onClick={toggleRead}
        disabled={busy}
      />
      <QuickAction icon={<Trash2 className="h-3.5 w-3.5" />} title="Cestina" onClick={(e) => runAction(e, () => trashThread(organizationId, userId, thread.thread_id))} disabled={busy} danger />
    </div>
  );

  const starButton = (
    <button
      type="button"
      onClick={handleStar}
      title={thread.is_starred ? 'Rimuovi preferito' : 'Aggiungi ai preferiti'}
      className="flex-shrink-0 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
    >
      <Star className={`h-4 w-4 ${thread.is_starred ? 'text-yellow-500 fill-yellow-500' : 'text-[#C4C4C0] dark:text-white/30'}`} />
    </button>
  );

  const rowBase = `group relative w-full text-left border-b border-[rgba(55,53,47,0.06)] dark:border-gray-800/50 transition-colors cursor-pointer ${
    isSelected ? 'bg-[#2383E2]/8 dark:bg-[#2383E2]/15' : 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/3'
  }`;

  // ─── Vista "righe lunghe" (stile Gmail, una riga) ───
  if (fullWidth) {
    return (
      <div
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
        className={`${rowBase} px-4 py-2.5`}
      >
        <div className="flex items-center gap-2.5">
          {checkbox}
          {starButton}

          {/* Mittente + conteggio thread */}
          <span className={`w-44 flex-shrink-0 truncate text-sm ${thread.has_unread ? 'font-semibold text-[#37352F] dark:text-white' : 'text-[#444] dark:text-white/70'}`}>
            {senderName}
            {thread.messages.length > 1 && (
              <span className="ml-1 text-[11px] text-[#9B9A97]">{thread.messages.length}</span>
            )}
            {hasDraft && <span className="ml-1 text-[11px] font-medium text-[#DC2626]">Bozza</span>}
          </span>

          {/* Etichette + oggetto + anteprima sulla stessa riga */}
          <div className="flex-1 min-w-0 flex items-center gap-1.5">
            {linkedClient && (
              <span className="flex-shrink-0 text-[10px] text-[#2383E2] bg-[#2383E2]/10 px-1.5 py-0.5 rounded">
                {linkedClient.business_name || linkedClient.contact_name || ''}
              </span>
            )}
            <LabelChips thread={thread} />
            <span className={`truncate text-sm ${thread.has_unread ? 'font-semibold text-[#37352F] dark:text-white' : 'text-[#37352F]/90 dark:text-white/80'}`}>
              {thread.subject}
            </span>
            <span className="truncate text-sm text-[#9B9A97] dark:text-white/40">
              — {latest.snippet}
            </span>
          </div>

          {/* Apertura tracciata */}
          {openGlyph}

          {/* Allegato */}
          {hasAttachment && <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-[#9B9A97] dark:text-white/40" />}

          {/* Data */}
          <div className="flex-shrink-0 w-[72px] flex items-center justify-end">
            <span className={`text-xs ${thread.has_unread ? 'font-semibold text-[#37352F] dark:text-white' : 'text-[#787774] dark:text-white/40'}`}>
              {dateStr}
            </span>
          </div>
        </div>

        {/* Chip allegati (riga sotto, come Gmail) */}
        <div className="pl-[26px]">
          <AttachmentChips thread={thread} />
        </div>

        {/* Azioni hover (overlay assoluto: non altera l'altezza della riga) */}
        <div className="absolute right-3 top-2.5 z-10">{quickActions}</div>
      </div>
    );
  }

  // ─── Vista compatta (lista stretta con anteprima a destra) ───
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      className={`${rowBase} px-4 py-3`}
    >
      {/* Quick actions on hover */}
      <div className="absolute top-2 right-3 z-10">{quickActions}</div>

      <div className="flex items-start gap-3">
        {checkbox && <div className="pt-0.5">{checkbox}</div>}
        {/* Avatar pastello */}
        <div
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden text-xs font-semibold"
          style={linkedClient?.logo_url ? undefined : { backgroundColor: `${color}26`, color }}
        >
          {linkedClient?.logo_url ? (
            <img src={linkedClient.logo_url} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <span>{initial}</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className={`text-sm truncate flex items-center gap-1 ${thread.has_unread ? 'font-bold text-[#37352F] dark:text-white' : 'font-normal text-[#787774] dark:text-white/60'}`}>
              <span className="truncate">{senderName}</span>
              {thread.messages.length > 1 && (
                <span className="flex-shrink-0 text-[11px] font-normal text-[#9B9A97]">{thread.messages.length}</span>
              )}
              {hasDraft && <span className="flex-shrink-0 text-[11px] font-medium text-[#DC2626]">Bozza</span>}
            </span>
            <span className={`text-xs flex-shrink-0 group-hover:opacity-0 transition-opacity ${thread.has_unread ? 'font-bold text-[#37352F] dark:text-white' : 'text-[#787774] dark:text-white/40'}`}>
              {dateStr}
            </span>
          </div>

          <div className={`text-sm truncate mb-0.5 ${thread.has_unread ? 'font-bold text-[#37352F] dark:text-white' : 'font-normal text-[#787774] dark:text-white/60'}`}>
            {thread.subject}
          </div>

          <div className={`text-xs truncate ${thread.has_unread ? 'text-[#37352F] dark:text-white/80' : 'text-[#787774] dark:text-white/40'}`}>
            {latest.snippet}
          </div>

          {/* Badges + etichette */}
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {thread.has_unread && (
              <span className="w-2 h-2 rounded-full" style={{ background: FLUSIA_GRADIENT }} />
            )}
            {starButton}
            {openGlyph}
            {hasAttachment && (
              <Paperclip className="h-3 w-3 text-[#787774] dark:text-white/40" />
            )}
            <LabelChips thread={thread} />
            {linkedClient && (
              <span className="text-[10px] text-[#2383E2] bg-[#2383E2]/10 px-1 rounded truncate max-w-[100px]">
                {linkedClient.business_name || linkedClient.contact_name || ''}
              </span>
            )}
          </div>

          {/* Chip allegati */}
          <AttachmentChips thread={thread} />
        </div>
      </div>
    </div>
  );
}

function QuickAction({
  icon,
  title,
  onClick,
  disabled,
  danger,
}: {
  icon: React.ReactNode;
  title: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded transition-colors disabled:opacity-40 ${
        danger
          ? 'text-[#787774] hover:text-[#DC2626] hover:bg-[#DC2626]/10'
          : 'text-[#787774] dark:text-white/60 hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10'
      }`}
    >
      {icon}
    </button>
  );
}
